// Enhances Summernote-lite's native image selection handle.
//
// Summernote already renders a selection box over a clicked image: a shaded
// background (.note-control-selection-bg) plus four corner squares
// (.note-control-nw/ne/sw/se), all mounted inside .note-editing-area and shown
// from its own `summernote.mousedown` handler. Out of the box, though:
//   - only the SE corner (.note-control-sizing) actually resizes; the other
//     three are decorative "holders";
//   - it calls preventDefault() on image mousedown, so the browser never
//     selects the image and Delete/Backspace does nothing;
//   - inside an animating host (the edit-reply modal) the box can be measured
//     before layout settles and end up misplaced until the next update.
//
// Rather than replacing that UI (a separate overlay has to fight the host's
// stacking contexts and never got a reliable paint), this module keeps it and:
//   1. makes NW/NE/SW resize too, aspect ratio preserved, anchored on the
//      opposite corner, driving Summernote's own `handle.update` so the box
//      follows the image;
//   2. selects the image node on click and deletes it on Delete/Backspace,
//      committing through `editor.afterCommand` so undo history and the
//      change → body sync behave exactly like a native edit;
//   3. re-runs `handle.update` after the click settles so the box shows,
//      correctly placed, on the very first click even inside a modal.
//
// Everything is resolved lazily per interaction (the editor initializes
// asynchronously and can swap its editable), and all listeners run in the
// capture phase because Summernote stops propagation on the bubbling path.

export interface SummernoteNoteApi {
  summernote: (command: string, ...args: unknown[]) => unknown;
}

type Corner = 'nw' | 'ne' | 'sw';

const CORNER_CLASSES: Record<Corner, string> = {
  nw: 'note-control-nw',
  ne: 'note-control-ne',
  sw: 'note-control-sw',
};

const MIN_WIDTH = 24; // px — never let an image collapse to nothing

// Delay for the second `handle.update` pass: long enough for a modal open
// transition to finish, short enough to be imperceptible.
const SETTLE_DELAY_MS = 200;

export function enableImageResizeHandles(
  getNote: () => SummernoteNoteApi | null | undefined,
  getEditable: () => HTMLElement | null | undefined,
): () => void
{
  let pendingSettleTimer: ReturnType<typeof setTimeout> | null = null;

  // Summernote's public API dispatches `module.method` strings to the module;
  // guarded because the editor may already be destroyed when an event lands.
  const invoke = (command: string, ...args: unknown[]): unknown =>
  {
    try
    {
      return getNote()?.summernote(command, ...args);
    }
    catch
    {
      return undefined;
    }
  };

  const editingAreaOf = (editable: HTMLElement): HTMLElement | null =>
    editable.closest<HTMLElement>('.note-editing-area');

  const selectionBoxOf = (editable: HTMLElement): HTMLElement | null =>
    editingAreaOf(editable)?.querySelector<HTMLElement>('.note-control-selection') ?? null;

  // The image Summernote currently has selected, if its box is showing
  const selectedImageIn = (editable: HTMLElement): HTMLImageElement | null =>
  {
    const box = selectionBoxOf(editable);
    if (!box || box.style.display === 'none' || box.offsetParent === null)
    {
      return null;
    }

    const target = invoke('editor.restoreTarget');
    return target instanceof HTMLImageElement && editable.contains(target) ? target : null;
  };

  // Selecting the node in the document selection is what lets Summernote's
  // mouseup `setLastRange()` record the image as the current range.
  const selectImageNode = (image: HTMLImageElement) =>
  {
    try
    {
      const range = document.createRange();
      range.selectNode(image);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    catch
    {
      // Detached or unselectable node — the handle box still works on its own
    }
  };

  // Show (or re-place) Summernote's box for this image once layout has settled.
  const scheduleHandleUpdate = (image: HTMLImageElement, event: MouseEvent) =>
  {
    if (pendingSettleTimer !== null)
    {
      clearTimeout(pendingSettleTimer);
      pendingSettleTimer = null;
    }

    const update = () =>
    {
      if (image.isConnected)
      {
        invoke('handle.update', image, event);
      }
    };

    requestAnimationFrame(update);
    pendingSettleTimer = setTimeout(() =>
    {
      pendingSettleTimer = null;
      update();
    }, SETTLE_DELAY_MS);
  };

  const cornerOf = (element: Element): Corner | null =>
  {
    for (const corner of Object.keys(CORNER_CLASSES) as Corner[])
    {
      if (element.classList.contains(CORNER_CLASSES[corner]))
      {
        return corner;
      }
    }
    return null;
  };

  // --- Resize from NW / NE / SW ---------------------------------------------
  const startCornerResize = (
    corner: Corner,
    image: HTMLImageElement,
    editable: HTMLElement,
    e: MouseEvent,
  ) =>
  {
    // Stop Summernote/the browser from treating this as a click in the editor
    e.preventDefault();
    e.stopPropagation();

    const startRect = image.getBoundingClientRect();
    const aspect = startRect.height > 0 ? startRect.width / startRect.height : 1;

    // The corner opposite the one being dragged stays put
    const anchorX = corner === 'ne' ? startRect.left : startRect.right;

    // Don't let the image grow wider than the editable content area
    const maxWidth = editable.clientWidth || startRect.width * 4;

    const onMouseMove = (moveEvent: MouseEvent) =>
    {
      let newWidth = Math.abs(moveEvent.clientX - anchorX);
      newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, maxWidth));
      const newHeight = newWidth / aspect;

      image.style.width = `${Math.round(newWidth)}px`;
      image.style.height = `${Math.round(newHeight)}px`;
      // Clear legacy width/height attributes so the inline style wins
      image.removeAttribute('width');
      image.removeAttribute('height');

      // Summernote repositions its box from the image's live rect
      invoke('handle.update', image, moveEvent);
    };

    const onMouseUp = (upEvent: MouseEvent) =>
    {
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);

      // Same commit path as Summernote's own SE resize: records undo and fires
      // `change`, which is what syncs the editor content back to React state.
      invoke('editor.afterCommand');

      // `change` makes Summernote hide the box; keep the image selected so the
      // user can keep adjusting or press Delete without clicking again.
      requestAnimationFrame(() =>
      {
        if (image.isConnected)
        {
          invoke('handle.update', image, upEvent);
        }
      });
    };

    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, true);
  };

  // --- Capture-phase mouse handling ----------------------------------------
  const onDocumentMouseDown = (e: MouseEvent) =>
  {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const editable = getEditable();
    if (!editable) return;

    // Click on an image in the editor: select it and make sure the box shows
    if (target instanceof HTMLImageElement && editable.contains(target))
    {
      selectImageNode(target);
      scheduleHandleUpdate(target, e);
      return;
    }

    // Drag start on one of the three non-native corners of OUR editor's box
    const corner = cornerOf(target);
    if (corner)
    {
      const box = selectionBoxOf(editable);
      if (!box || !box.contains(target)) return;

      const image = selectedImageIn(editable);
      if (image)
      {
        startCornerResize(corner, image, editable, e);
      }
    }
  };

  // --- Delete / Backspace removes the selected image ------------------------
  const onDocumentKeyDown = (e: KeyboardEvent) =>
  {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;

    const editable = getEditable();
    if (!editable) return;

    const image = selectedImageIn(editable);
    if (!image) return;

    e.preventDefault();
    e.stopPropagation();

    // Leave the caret where the image was so typing continues naturally
    const caret = document.createRange();
    caret.setStartAfter(image);
    caret.collapse(true);

    image.remove();
    invoke('handle.hide');

    try
    {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(caret);
    }
    catch
    {
      // Range anchored to the removed node — the editor keeps its own caret
    }

    invoke('editor.afterCommand');
  };

  document.addEventListener('mousedown', onDocumentMouseDown, true);
  document.addEventListener('keydown', onDocumentKeyDown, true);

  return () =>
  {
    document.removeEventListener('mousedown', onDocumentMouseDown, true);
    document.removeEventListener('keydown', onDocumentKeyDown, true);
    if (pendingSettleTimer !== null)
    {
      clearTimeout(pendingSettleTimer);
    }
  };
}
